import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import '@/features/f0/styles/bodyFonts'
import { useReferenceStyles } from './useReferenceStyles'
import { ProtoButton } from '@/features/d0/states'
import '@/features/u2/company.css'
import { useAuthStore } from '@/stores/authStore'
import { PlanPicker } from '../PlanPicker'
import type { TierKey } from '../pricing'
import '../u6.css'

export interface TrialExpiredOverlayProps {
  isOpen: boolean
  onPlanSelected: (addCompanyIntelligence?: boolean) => void
}

// Blocking overlay: the trial is over and there is no paid plan. Escape does not dismiss it.
export default function TrialExpiredOverlay({ isOpen, onPlanSelected }: TrialExpiredOverlayProps) {
  useReferenceStyles()
  const navigate = useNavigate()
  const logout = useAuthStore((s) => s.logout)
  const [step, setStep] = useState<'plans' | 'company'>('plans')
  const [paidMessage, setPaidMessage] = useState<string | null>(null)
  const titleRef = useRef<HTMLHeadingElement>(null)

  useEffect(() => {
    if (isOpen) titleRef.current?.focus()
  }, [isOpen, step])

  if (!isOpen) return null

  const onPaid = (outcome: { kind: string; message?: string }, tier: TierKey) => {
    if (tier === 'basic') {
      onPlanSelected(false)
      return
    }
    setPaidMessage(outcome.message ?? null)
    setStep('company')
  }

  const signOut = async () => {
    await logout()
    navigate('/login', { replace: true })
  }

  return (
    <div id="zupa-complete" className="zupa-app z-ov" role="dialog" aria-modal="true" aria-labelledby="trial-expired-title">
      <section className="s7-panel z-ov__card z-ov__card--wide">
        {step === 'plans' ? (
          <>
            <h2 id="trial-expired-title" tabIndex={-1} ref={titleRef}>
              Your free trial has ended
            </h2>
            <p>Choose a plan to keep creating posts. Your workspaces and drafts are kept.</p>
            <PlanPicker onPaid={onPaid} />
            <div className="z-ov__actions">
              <ProtoButton className="z-quiet" onClick={signOut}>Sign out and use a different account</ProtoButton>
            </div>
          </>
        ) : (
          <>
            <h2 id="trial-expired-title" tabIndex={-1} ref={titleRef}>
              Set up your company details?
            </h2>
            {paidMessage ? <p role="status">{paidMessage}</p> : null}
            <p>
              Your plan can use company intelligence: posts written from your company, services and audience.
              You can add it now or later from Settings.
            </p>
            <div className="z-ov__actions">
              <ProtoButton className="z-primary" onClick={() => onPlanSelected(true)}>Yes, add company details</ProtoButton>
              <ProtoButton onClick={() => onPlanSelected(false)}>Later</ProtoButton>
            </div>
          </>
        )}
      </section>
    </div>
  )
}
